import axios, { AxiosRequestConfig, HttpStatusCode } from "axios";

import { errors } from "../../constants";
import {
  AUTH_CREDENTIAL_ATTEMPT_PATH_MARKERS,
  SESSION_ENDED_TRANSLATION_KEYS,
} from "../../constants/auth.constants";
import { ApiException, IApiError } from "../../models";
import { isLoginPage } from "../auth/auth.helpers";

export const isAuthCredentialAttemptRequestUrl = (
  fullPathLower: string,
): boolean =>
  AUTH_CREDENTIAL_ATTEMPT_PATH_MARKERS.some(marker => {
    const idx = fullPathLower.indexOf(marker);
    if (idx === -1) {
      return false;
    }
    const next = fullPathLower[idx + marker.length];
    return next === undefined || next === "/" || next === "?" || next === "#";
  });

export const getAxiosConfigFullPathLower = (
  config: AxiosRequestConfig,
): string => {
  try {
    return axios.getUri(config).toLowerCase();
  } catch {
    const base = (config.baseURL ?? "").replace(/\/$/, "");
    const path = (config.url ?? "").replace(/^\//, "");
    return `${base}/${path}`.replace(/\/+/g, "/").toLowerCase();
  }
};

export const shouldRedirectToLoginAfterSessionEnded = ({
  translationKey,
  statusCode,
  requestFullUrlLower,
}: {
  translationKey?: string;
  statusCode?: number;
  /** From {@link getAxiosConfigFullPathLower}; omit only when the caller cannot know the URL (then {@link errors.auth.authenticationFailure} is not treated as session end). */
  requestFullUrlLower?: string;
}): boolean => {
  if (!translationKey || isLoginPage()) {
    return false;
  }
  if (SESSION_ENDED_TRANSLATION_KEYS.has(translationKey)) {
    return true;
  }
  if (
    translationKey === errors.auth.authenticationFailure &&
    statusCode === HttpStatusCode.Unauthorized &&
    requestFullUrlLower !== undefined &&
    !isAuthCredentialAttemptRequestUrl(requestFullUrlLower)
  ) {
    return true;
  }
  return false;
};

export const shouldRedirectToLoginForApiException = ({
  apiError,
  axiosConfig,
}: {
  apiError: unknown;
  axiosConfig: AxiosRequestConfig;
}): boolean => {
  if (!(apiError instanceof ApiException)) {
    return false;
  }
  return shouldRedirectToLoginAfterSessionEnded({
    translationKey: apiError.translationKey,
    statusCode: apiError.statusCode,
    requestFullUrlLower: getAxiosConfigFullPathLower(axiosConfig),
  });
};

export const shouldRedirectToLoginForAxiosUnauthorized = (
  error: unknown,
  axiosConfig: AxiosRequestConfig,
): boolean => {
  if (!axios.isAxiosError(error)) {
    return false;
  }
  if (error.response?.status !== HttpStatusCode.Unauthorized) {
    return false;
  }
  const data = error.response?.data as IApiError | undefined;
  return shouldRedirectToLoginAfterSessionEnded({
    translationKey: data?.translationKey,
    statusCode: HttpStatusCode.Unauthorized,
    requestFullUrlLower: getAxiosConfigFullPathLower(axiosConfig),
  });
};

/**
 * Hooks that catch  without request URL: session-ended keys only, plus 401 authenticationFailure.
 */
export const shouldRedirectToLoginForCaughtApiException = (
  apiError: unknown,
): boolean => {
  if (!(apiError instanceof ApiException)) {
    return false;
  }
  if (isLoginPage()) {
    return false;
  }
  const key = apiError.translationKey;
  if (!key) {
    return false;
  }
  if (SESSION_ENDED_TRANSLATION_KEYS.has(key)) {
    return true;
  }
  return (
    key === errors.auth.authenticationFailure &&
    apiError.statusCode === HttpStatusCode.Unauthorized
  );
};
